import { Button } from "@/components/ui/Button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/Form";
import { Input } from "@/components/ui/Input";
import { useUpdateAdminCurrency } from "@/hooks/services/use-currency";
import {
  adminCurrencyFormSchema,
  adminCurrencyFormValues,
} from "@/schemas/currency";
import { AdminCurrency } from "@/services/core/admin-currency.api";
import { useDialogStore } from "@/store/dialog-store";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

interface UpdateAdminCurrencyFormProps {
  selectedCurrency: AdminCurrency | null;
  setSelectedCurrency: React.Dispatch<
    React.SetStateAction<AdminCurrency | null>
  >;
  initialData: {
    currencyCode: string;
    displayName: string;
  };
}
const UpdateAdminCurrencyForm = ({
  selectedCurrency,
  setSelectedCurrency,
  initialData,
}: UpdateAdminCurrencyFormProps) => {
  const updateMutation = useUpdateAdminCurrency();
  const { closeUpdateDialog } = useDialogStore();
  const form = useForm<adminCurrencyFormValues>({
    resolver: zodResolver(adminCurrencyFormSchema),
    defaultValues: {
      currencyCode: initialData.currencyCode,
      displayName: initialData.displayName,
    },
  });

  useEffect(() => {
    form.reset({
      currencyCode: initialData.currencyCode,
      displayName: initialData.displayName,
    });
  }, [form, initialData]);

  const onSubmit = async (values: adminCurrencyFormValues) => {
    if (!selectedCurrency) return;

    const data = {
      nonce: "string",
      payload: values,
    };

    try {
      await updateMutation.mutateAsync({
        id: selectedCurrency.id,
        data,
      });

      toast.success("Update currency successful!");
    } catch (error) {
      console.error("Failed to update currency:", error);
    }

    closeUpdateDialog();
    setSelectedCurrency(null);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="currencyCode"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Currency Code</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder="Enter currency code (e.g., USD)"
                  maxLength={6}
                  className="uppercase"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="displayName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Display Name</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder="Enter display name (e.g., US Dollar)"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={closeUpdateDialog}>
            Cancel
          </Button>
          <Button type="submit" disabled={updateMutation.isPending}>
            {updateMutation.isPending ? "Updating..." : "Update Currency"}
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default UpdateAdminCurrencyForm;
